import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { Button, Field, Notice } from '../../design-system/components';
import { formatCnpj, formatCpf, serviceCustomerName, validCnpj, validCpf } from './helpers';
import type { Bootstrap, Client, PersonType, Project, ServiceCode } from '../../types/contracts';

export function NewProposal({ bootstrap, onCreated }: { bootstrap: Bootstrap; onCreated: (id: string) => void }) {
  const [personType, setPersonType] = useState<PersonType>('PF');
  const [service, setService] = useState<ServiceCode>('S1');
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [projectMode, setProjectMode] = useState<'none' | 'existing' | 'new'>('none');
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [query, setQuery] = useState('');
  const [taxId, setTaxId] = useState('');
  const [signatoryCpf, setSignatoryCpf] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get<Client[]>('/clients').then(setClients);
    api.get<Project[]>('/projects').then(setProjects);
  }, []);
  const filtered = useMemo(
    () =>
      clients.filter((c) => !query || `${c.name} ${c.legal_name} ${c.trade_name} ${c.tax_id}`.toLowerCase().includes(query.toLowerCase())),
    [clients, query],
  );
  const selected = bootstrap.services[service];
  const clientProjects = projects.filter((p) => !clientId || p.client_id === clientId);
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError('');
    const f = new FormData(e.currentTarget);
    if (mode === 'new' && personType === 'PJ' && !validCnpj(taxId)) {
      setBusy(false);
      setError('Informe um CNPJ válido.');
      return;
    }
    if (mode === 'new' && personType === 'PF' && taxId && !validCpf(taxId)) {
      setBusy(false);
      setError('Informe um CPF válido.');
      return;
    }
    if (mode === 'new' && personType === 'PJ' && signatoryCpf && !validCpf(signatoryCpf)) {
      setBusy(false);
      setError('Informe um CPF válido para o representante signatário ou deixe o campo vazio.');
      return;
    }
    const objectText = String(f.get('object_text') || '');
    const body: any = {
      service_code: service,
      object_text: objectText,
      client_id: mode === 'existing' ? clientId || null : null,
      project_id: projectMode === 'existing' ? projectId || null : null,
    };
    if (mode === 'new')
      body.client = {
        person_type: personType,
        name: String(f.get('name') || ''),
        legal_name: personType === 'PJ' ? String(f.get('name') || '') : '',
        trade_name: String(f.get('trade_name') || ''),
        tax_id: taxId,
        email: String(f.get('email') || ''),
        phone: String(f.get('phone') || ''),
        address_json: { city: String(f.get('city') || ''), state: String(f.get('state') || '') },
        contacts_json: {
          primary: {
            name: String(f.get('contact_name') || '') || String(f.get('name') || ''),
            role: String(f.get('contact_role') || ''),
            email: String(f.get('email') || ''),
            phone: String(f.get('phone') || ''),
          },
          ...(personType === 'PJ' ? {
            signatory: {
              name: String(f.get('signatory_name') || ''),
              tax_id: signatoryCpf,
              role: String(f.get('signatory_role') || ''),
            },
          } : {}),
        },
        confirm_duplicate: f.get('confirm_duplicate') === 'on',
      };
    if (projectMode === 'new')
      body.project = {
        name: String(f.get('project_name') || objectText),
        location: String(f.get('project_location') || f.get('city') || ''),
        confirm_duplicate: f.get('confirm_project_duplicate') === 'on',
      };
    try {
      const r = await api.post<{ proposal_id: string }>('/proposals/guided-draft', body);
      onCreated(r.proposal_id);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="new-proposal-page">
      <header className="page-intro">
        <span className="eyebrow">Nova proposta</span>
        <h1>Comece pelo cliente, pelo objeto e pelo serviço</h1>
        <p>O rascunho será criado depois que as informações mínimas forem confirmadas.</p>
      </header>
      {error && (
        <Notice tone="danger" title="Não foi possível criar o rascunho">
          {error}
        </Notice>
      )}
      <form onSubmit={submit} className="new-proposal-form">
        <section className="initial-card">
          <div className="section-heading">
            <span>1</span>
            <div>
              <h2>Cliente</h2>
              <p>Busque um cadastro existente antes de criar outro.</p>
            </div>
          </div>
          <div className="segmented">
            <button type="button" className={mode === 'existing' ? 'selected' : ''} onClick={() => setMode('existing')}>
              Selecionar existente
            </button>
            <button type="button" className={mode === 'new' ? 'selected' : ''} onClick={() => setMode('new')}>
              Cadastrar novo
            </button>
          </div>
          {mode === 'existing' ? (
            <div className="registry-picker vertical">
              <Field id="client-search" label="Buscar cliente">
                <input
                  id="client-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nome, razão social ou documento"
                />
              </Field>
              <h3>Resultados encontrados</h3>
              <div className="picker-list">
                {filtered.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    className={clientId === c.id ? 'selected' : ''}
                    onClick={() => {
                      setClientId(c.id);
                      setPersonType(c.person_type);
                      setProjectId('');
                    }}
                  >
                    <strong>{c.legal_name || c.name}</strong>
                    <span>
                      {c.person_type} {c.tax_id_formatted && `· ${c.tax_id_formatted}`}
                    </span>
                  </button>
                ))}
                {!filtered.length && <p>Nenhum cliente encontrado. Use “Cadastrar novo”.</p>}
              </div>
            </div>
          ) : (
            <>
              <div className="segmented" role="radiogroup">
                <button
                  type="button"
                  className={personType === 'PF' ? 'selected' : ''}
                  onClick={() => {
                    setPersonType('PF');
                    setTaxId('');
                  }}
                >
                  Pessoa física
                </button>
                <button
                  type="button"
                  className={personType === 'PJ' ? 'selected' : ''}
                  onClick={() => {
                    setPersonType('PJ');
                    setTaxId('');
                  }}
                >
                  Pessoa jurídica
                </button>
              </div>
              <div className="form-grid">
                {personType === 'PF' ? (
                  <Field id="name" label="Nome" required>
                    <input id="name" name="name" required />
                  </Field>
                ) : (
                  <>
                    <Field id="name" label="Razão social" required>
                      <input id="name" name="name" required />
                    </Field>
                    <Field id="trade_name" label="Nome fantasia">
                      <input id="trade_name" name="trade_name" />
                    </Field>
                  </>
                )}
                <Field
                  id="tax_id"
                  label={personType === 'PF' ? 'CPF, quando já disponível' : 'CNPJ'}
                  required={personType === 'PJ'}
                  error={
                    taxId && (personType === 'PF' ? !validCpf(taxId) : !validCnpj(taxId))
                      ? `Informe um ${personType === 'PF' ? 'CPF' : 'CNPJ'} válido.`
                      : undefined
                  }
                >
                  <input
                    id="tax_id"
                    name="tax_id"
                    inputMode="numeric"
                    value={taxId}
                    onChange={(e) => setTaxId(personType === 'PF' ? formatCpf(e.target.value) : formatCnpj(e.target.value))}
                    required={personType === 'PJ'}
                  />
                </Field>
                <Field id="contact_name" label="Contato principal">
                  <input id="contact_name" name="contact_name" />
                </Field>
                <Field id="contact_role" label="Função, quando aplicável">
                  <input id="contact_role" name="contact_role" />
                </Field>
                {personType === 'PJ' && <>
                  <Field id="signatory_name" label="Representante signatário, opcional">
                    <input id="signatory_name" name="signatory_name" />
                  </Field>
                  <Field id="signatory_tax_id" label="CPF do representante, opcional" error={signatoryCpf && !validCpf(signatoryCpf) ? 'Informe um CPF válido ou deixe o campo vazio.' : undefined}>
                    <input id="signatory_tax_id" name="signatory_tax_id" inputMode="numeric" value={signatoryCpf} onChange={(e) => setSignatoryCpf(formatCpf(e.target.value))} />
                  </Field>
                  <Field id="signatory_role" label="Cargo ou função do representante, opcional">
                    <input id="signatory_role" name="signatory_role" />
                  </Field>
                </>}
                <Field id="email" label="E-mail">
                  <input id="email" name="email" type="email" />
                </Field>
                <Field id="phone" label="Telefone">
                  <input id="phone" name="phone" />
                </Field>
                <Field id="city" label="Cidade ou local">
                  <input id="city" name="city" />
                </Field>
                <Field id="state" label="UF">
                  <input id="state" name="state" maxLength={2} />
                </Field>
              </div>
              <label className="confirm-line">
                <input type="checkbox" name="confirm_duplicate" />
                Confirmo criar novo cadastro mesmo se houver nome semelhante.
              </label>
            </>
          )}
        </section>
        <section className="initial-card">
          <div className="section-heading">
            <span>2</span>
            <div>
              <h2>Projeto ou objeto</h2>
              <p>O objeto é obrigatório; o cadastro do projeto pode ser vinculado agora ou depois.</p>
            </div>
          </div>
          <Field id="object_text" label="Projeto ou objeto" required>
            <textarea id="object_text" name="object_text" required />
          </Field>
          <div className="segmented">
            <button type="button" className={projectMode === 'none' ? 'selected' : ''} onClick={() => setProjectMode('none')}>
              Sem projeto cadastrado
            </button>
            <button type="button" className={projectMode === 'existing' ? 'selected' : ''} onClick={() => setProjectMode('existing')}>
              Selecionar projeto
            </button>
            <button type="button" className={projectMode === 'new' ? 'selected' : ''} onClick={() => setProjectMode('new')}>
              Cadastrar projeto
            </button>
          </div>
          {projectMode === 'existing' && (
            <div className="picker-list">
              {clientProjects.map((p) => (
                <button type="button" key={p.id} className={projectId === p.id ? 'selected' : ''} onClick={() => setProjectId(p.id)}>
                  <strong>{p.name}</strong>
                  <span>
                    {p.code} · {p.location}
                  </span>
                </button>
              ))}
              {!clientProjects.length && <p>Nenhum projeto disponível para este cliente.</p>}
            </div>
          )}
          {projectMode === 'new' && (
            <div className="form-grid">
              <Field id="project_name" label="Nome do projeto">
                <input id="project_name" name="project_name" />
              </Field>
              <Field id="project_location" label="Local">
                <input id="project_location" name="project_location" />
              </Field>
              <label className="confirm-line full">
                <input type="checkbox" name="confirm_project_duplicate" />
                Confirmo criar novo projeto mesmo se houver nome semelhante.
              </label>
            </div>
          )}
        </section>
        <section className="initial-card">
          <div className="section-heading">
            <span>3</span>
            <div>
              <h2>Serviço</h2>
              <p>Selecione o serviço que corresponde ao objeto desta proposta.</p>
            </div>
          </div>
          <div className="service-grid" role="radiogroup">
            {Object.values(bootstrap.services).map((s) => (
              <button
                type="button"
                role="radio"
                aria-checked={service === s.code}
                key={s.code}
                className={`service-option ${service === s.code ? 'selected' : ''}`}
                onClick={() => setService(s.code)}
              >
                <input className="sr-only" tabIndex={-1} type="radio" value={s.code} checked={service === s.code} readOnly />
                <span className="service-code">{s.code}</span>
                <strong>{serviceCustomerName(s.code, s)}</strong>
                <span>{s.description}</span>
                <small>{s.purpose}</small>
              </button>
            ))}
          </div>
        </section>
        <div className="sticky-action">
          <div>
            <strong>Próxima ação</strong>
            <span>Criar o rascunho e iniciar a construção de {serviceCustomerName(selected.code, selected)}.</span>
          </div>
          <Button disabled={busy || (mode === 'existing' && !clientId)}>{busy ? 'Criando...' : 'Criar rascunho e construir'}</Button>
        </div>
      </form>
    </main>
  );
}
